import { HashRouter, Navigate, Route, Routes, Outlet, useParams } from 'react-router';
import { lazy, Suspense, useContext } from 'react';
import { PrintableProvider } from '@matrix-forge/ui/contexts/PrintableContext';
import { ForwardDocumentProviderConnected } from '@/providers/ForwardDocumentProviderConnected';
import { RequireAdmin } from '@/components/RequireAdmin';
import { RequirePaidPlan } from '@/components/RequirePaidPlan';
import { UserInfoContext } from '@/contexts/UserInfoContext';

// Lazy load all route components for code-splitting
const RootRoute = lazy(() => import('@/routes/RootRoute').then(m => ({ default: m.RootRoute })));
const PersonaRoutes = {
  Root: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Root }))),
  ConversationNewOrChat: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.ConversationNewOrChat }))
  ),
  ConversationFallback: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.ConversationFallback }))
  ),
  History: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.History }))
  ),
  Documents: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Documents }))
  ),
  Jobs: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Jobs }))),
  Workflows: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Workflows }))
  ),
  Configuration: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Configuration }))
  ),
  Auto: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Auto }))),
};
const DocumentRoute = lazy(() =>
  import('@/routes/DocumentRoute').then(m => ({ default: m.DocumentRoute }))
);
const DocumentsRoute = lazy(() =>
  import('@/routes/DocumentsRoute').then(m => ({ default: m.DocumentsRoute }))
);
const AdminRoutes = {
  KnowledgeBase: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.KnowledgeBase }))
  ),
  KnowledgeBaseDetail: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.KnowledgeBaseDetail }))
  ),
  PersonasRoot: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasRoot }))
  ),
  PersonasIndex: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasIndex }))
  ),
  PersonasGroups: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasGroups }))
  ),
  PersonasAuto: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasAuto }))
  ),
  Secrets: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Secrets }))
  ),
  McpServers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.McpServers }))
  ),
  Webhooks: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Webhooks }))
  ),
  WorkflowBuilder: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.WorkflowBuilder }))
  ),
  GatewayConfig: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.GatewayConfig }))
  ),
  WorkflowOverview: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.WorkflowOverview }))
  ),
  Triggers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Triggers }))
  ),
  TeamMembers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.TeamMembers }))
  ),
};
const FallbackRoute = lazy(() =>
  import('@/routes/FallbackRoute').then(m => ({ default: m.FallbackRoute }))
);
const PrintRoute = lazy(() => import('@/routes/PrintRoute').then(m => ({ default: m.PrintRoute })));
const JobInstancesRoute = lazy(() =>
  import('@/routes/JobInstancesRoute').then(m => ({ default: m.JobInstancesRoute }))
);
const JobInstanceRoute = lazy(() =>
  import('@/routes/JobInstanceRoute').then(m => ({ default: m.JobInstanceRoute }))
);
const WorkflowsRoute = lazy(() =>
  import('@/routes/WorkflowsRoute').then(m => ({ default: m.WorkflowsRoute }))
);
const AppsRoute = lazy(() => import('@/routes/AppsRoute').then(m => ({ default: m.AppsRoute })));
const AppViewerRoute = lazy(() =>
  import('@/routes/AppViewerRoute').then(m => ({ default: m.AppViewerRoute }))
);
const NewWorkflowRoute = lazy(() =>
  import('@/routes/WorkflowsRoute').then(m => ({ default: m.NewWorkflowRoute }))
);
const WorkflowRoute = lazy(() =>
  import('@/routes/WorkflowRoute').then(m => ({ default: m.WorkflowRoute }))
);
const ExecuteWorkflowRoute = lazy(() =>
  import('@/routes/ExecuteWorkflowRoute').then(m => ({ default: m.ExecuteWorkflowRoute }))
);
const ExecutionRoute = lazy(() =>
  import('@/routes/ExecutionRoute').then(m => ({ default: m.ExecutionRoute }))
);
const CasesRoute = lazy(() =>
  import('@/routes/cases_route').then(m => ({ default: m.CasesRoute }))
);
const SearchRoute = lazy(() =>
  import('@/routes/search_route').then(m => ({ default: m.SearchRoute }))
);
const AdminCasesRoute = lazy(() =>
  import('@/routes/AdminCasesRoute').then(m => ({ default: m.AdminCasesRoute }))
);

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-gray-500">Loading...</div>
  </div>
);

// Wrapper to force ExecuteWorkflowRoute to remount when workflowId changes
const ExecuteWorkflowRouteWithKey = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  return <ExecuteWorkflowRoute key={workflowId} />;
};

export const AppRouter = () => {
  const { userInfo } = useContext(UserInfoContext);
  const isPlg = !!userInfo?.isPlg;

  return (
    /* PrintableProvider intialPathname must match the path base for the print route */
    <PrintableProvider initialPathname="print-preview/document">
      <HashRouter>
        <ForwardDocumentProviderConnected>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="*" element={<RootRoute />}>
                <Route path="persona" element={<Outlet />}>
                  <Route path=":personaId" element={<PersonaRoutes.Root />}>
                    <Route path="conversation" element={<Outlet />}>
                      <Route path="_new" element={<PersonaRoutes.ConversationNewOrChat />} />
                      <Route
                        path=":conversationId"
                        element={<PersonaRoutes.ConversationNewOrChat />}
                      />
                      <Route path="*" element={<PersonaRoutes.ConversationFallback />} />
                    </Route>
                    <Route path="history" element={<PersonaRoutes.History />} />
                    <Route path="documents" element={<PersonaRoutes.Documents />} />
                    <Route path="jobs" element={<PersonaRoutes.Jobs />} />
                    <Route path="workflows" element={<PersonaRoutes.Workflows />} />
                    <Route path="config" element={<PersonaRoutes.Configuration />} />
                    <Route path="*" element={<PersonaRoutes.Auto fallback="conversation" />} />
                  </Route>
                </Route>
                <Route
                  path="documents"
                  element={
                    <RequirePaidPlan>
                      <Outlet />
                    </RequirePaidPlan>
                  }
                >
                  <Route path=":conversationId/:documentId" element={<DocumentRoute />} />
                  <Route path="*" element={<DocumentsRoute />} />
                </Route>
                <Route path="jobs" element={<JobInstancesRoute />} />
                <Route path="job/:jobId" element={<JobInstanceRoute />} />
                <Route path="workflows" element={<Outlet />}>
                  <Route index element={<WorkflowsRoute />} />
                  <Route path="new" element={<NewWorkflowRoute />} />
                  <Route path=":workflowId" element={<WorkflowRoute />} />
                  <Route path=":workflowId/execute" element={<ExecuteWorkflowRouteWithKey />} />
                </Route>
                <Route path="executions/:executionId" element={<ExecutionRoute />} />
                <Route path="apps" element={<AppsRoute />} />
                <Route path="apps/:appAddress" element={<AppViewerRoute />} />
                <Route
                  path="cases/*"
                  element={
                    <RequirePaidPlan>
                      <CasesRoute />
                    </RequirePaidPlan>
                  }
                />
                <Route
                  path="search/*"
                  element={
                    <RequirePaidPlan>
                      <SearchRoute />
                    </RequirePaidPlan>
                  }
                />
                {/* Team settings: hidden for PLG (no team), gated by RBAC for enterprise */}
                {!isPlg && (
                  <Route path="settings" element={<Outlet />}>
                    {userInfo?.features?.rbacEnabled && (
                      <Route
                        path="team"
                        element={
                          <RequireAdmin>
                            <AdminRoutes.TeamMembers />
                          </RequireAdmin>
                        }
                      />
                    )}
                  </Route>
                )}
                <Route path="admin" element={<Outlet />}>
                  <Route
                    path="cases/*"
                    element={
                      <RequirePaidPlan>
                        <AdminCasesRoute />
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="knowledge-bases"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.KnowledgeBase />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="knowledge-bases/knowledge-base/:id"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.KnowledgeBaseDetail />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="personas"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.PersonasRoot />
                      </RequireAdmin>
                    }
                  >
                    <Route path="groups" element={<AdminRoutes.PersonasGroups />} />
                    <Route path="index" element={<AdminRoutes.PersonasIndex />} />
                    <Route path="*" element={<AdminRoutes.PersonasAuto fallback="index" />} />
                  </Route>
                  <Route
                    path="secrets"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.Secrets />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="mcp-servers"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.McpServers />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="webhooks"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Webhooks />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="triggers"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Triggers />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="workflow_builder"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.WorkflowBuilder />
                      </RequireAdmin>
                    }
                  />
                  <Route path="workflow-overview" element={<AdminRoutes.WorkflowOverview />} />
                  <Route
                    path="gateways"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.GatewayConfig />
                      </RequireAdmin>
                    }
                  />
                  <Route path="*" element={<Navigate to="/admin/personas" replace />} />
                </Route>
                <Route
                  path="print-preview/document/:conversationId/:documentId"
                  element={<PrintRoute />}
                />
                <Route path="*" element={<FallbackRoute />} />
              </Route>
            </Routes>
          </Suspense>
        </ForwardDocumentProviderConnected>
      </HashRouter>
    </PrintableProvider>
  );
};
